import type { RouterOutputs } from '../lib/trpc'
import { Notice } from './index'
import './aliya-name-reading.css'

type Reading = RouterOutputs['member']['parseAliyaName']
type Question = NonNullable<Reading>['questions'][number]

/**
 * What the line did not settle, said to the person who can settle it.
 *
 * Each names the doubt and the reading that was taken, because a question
 * without the answer it guessed leaves somebody checking a name they cannot
 * see.
 */
const QUESTIONS: Record<Question, string> = {
  yichus:
    'Nothing in the line says kohen, levi or yisrael, so none is assumed. Say which below.',
  split:
    'More than one word could have been the “son of”. The last one was taken.',
  compound:
    'A בן or בת reads both as a name and as “son of”. Check the two halves below.',
}

type Parts = NonNullable<Reading>['parts']

/** The word the separator was read as, and so the gender it carried. */
const CALLED: Record<NonNullable<Parts['gender']>, string> = {
  male: 'בן',
  female: 'בת',
}

/* Total over the yichus values, so a new one is a compile error here rather
   than a raw key rendered on screen. */
const YICHUS: Record<NonNullable<Parts['yichus']>, string> = {
  kohen: 'Kohen',
  levi: 'Levi',
  yisrael: 'Yisrael',
}

/**
 * The parts in the order they stand in the LINE, which is the order somebody
 * checks them in: they read along what they typed and match each word to the
 * part it landed in. Sorting them by anything else would make that a hunt.
 */
function partRows(parts: Parts) {
  const rows: { key: string; label: string; value: string; he: boolean }[] = []
  /** A part of the line itself, and so set and read as Hebrew. */
  const fromTheLine = (key: string, label: string, value: string | null) => {
    if (value) rows.push({ key, label, value, he: true })
  }
  fromTheLine('honorific', 'Title', parts.honorificHe)
  fromTheLine('name', 'Name', parts.hebrewName)
  fromTheLine('called', 'Called', parts.gender ? CALLED[parts.gender] : null)
  fromTheLine('father-honorific', "Father's title", parts.fatherHonorificHe)
  fromTheLine('father-name', "Father's name", parts.fatherHebrewName)
  /* Always drawn, and "Not said" is a real answer: an absent designation is the
     one thing the line cannot state, so a row missing here would read as the
     parse having nothing to say rather than as a question to answer. */
  rows.push({
    key: 'yichus',
    label: 'Yichus',
    value: parts.yichus ? YICHUS[parts.yichus] : 'Not said',
    he: false,
  })
  return rows
}

/**
 * What the app understood a typed shem la'aliya to say, shown back before
 * anything is stored.
 *
 * The roster composes every line it prints from these parts and from no stored
 * string, so a wrong split here is a wrong name called out in shul — and the
 * only moment it is cheap to catch is while the person who owns the name is
 * still looking at it.
 *
 * It states the parts rather than re-composing the line: a line that reads back
 * correctly can still have been cut in the wrong place, and the cut is the thing
 * being checked.
 *
 * Read-only. Correcting a reading is done in the fields it names, which is where
 * the answer has to land anyway.
 */
export function AliyaNameReading({ reading }: { reading: Reading }) {
  if (!reading) {
    return (
      <Notice tone="note">
        Not enough to go on yet — a name, and his father's name after בן.
      </Notice>
    )
  }

  const { parts, questions } = reading

  return (
    <div className="aliyaread">
      <dl className="aliyaread__parts">
        {partRows(parts).map(({ key, label, value, he }) => (
          <div className="aliyaread__part" key={key}>
            <dt className="aliyaread__label">{label}</dt>
            {/* `he` isolates the value's direction: a Hebrew name set inside an
                LTR row otherwise takes its neighbors' direction at the edges. */}
            <dd
              className={he ? 'aliyaread__value he' : 'aliyaread__value'}
              lang={he ? 'he' : undefined}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>
      {questions.map((question) => (
        <Notice tone="note" key={question}>
          {QUESTIONS[question]}
        </Notice>
      ))}
    </div>
  )
}
